import React, { useState } from "react";
import { withRouter } from "react-router";
import { Empty, Table, Form, Input, InputNumber, Select, Switch, Tag, Tooltip, Space, message } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  LockOutlined,
  FileOutlined,
  FormOutlined,
  NumberOutlined,
  DownOutlined,
  PhoneOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { showConfirmDlgWidget } from "components/ConfirmDlgWidget/showConfirmDlgWidget";
import "./FormUiTable.css";

const { Option } = Select;

// Map UI type codes to readable labels and icons
const UI_TYPE_MAP = {
  "1": { label: "Text", icon: <FormOutlined />, color: "blue" },
  "2": { label: "Number", icon: <NumberOutlined />, color: "green" },
  "3": { label: "Dropdown", icon: <DownOutlined />, color: "purple" },
  "4": { label: "Phone", icon: <PhoneOutlined />, color: "cyan" },
  "5": { label: "Button", icon: <FileOutlined />, color: "orange" },
  "6": { label: "Date", icon: <CalendarOutlined />, color: "magenta" },
  "7": { label: "Date Input", icon: <CalendarOutlined />, color: "geekblue" },
};

// Group type labels
const GROUP_TYPE_MAP = {
  "1": "Participant Info",
  "2": "Parent/Guardian",
  "3": "Emergency Contact",
  "4": "Other Info",
};

// Editable cell component
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  children,
  options,
  ...restProps
}) => {
  let inputNode;

  switch (inputType) {
    case "number":
      inputNode = <InputNumber min={1} max={100} size="small" style={{ width: "100%" }} />;
      break;
    case "select":
      inputNode = (
        <Select size="small" style={{ width: "100%" }}>
          {options?.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      );
      break;
    case "switch":
      inputNode = <Switch size="small" />;
      break;
    default:
      inputNode = <Input size="small" />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          valuePropName={inputType === "switch" ? "checked" : "value"}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

function FormUiTable(props) {
  const { dataList, deleteData, updateData } = props;
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");

  if (dataList === undefined) return <div />;

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      name: record.name || "",
      hint_text: record.hint_text || "",
      order: record.order || 1,
      ui_type: record.ui_type || "1",
      is_required: record.is_required === "true",
      is_encrypt: record.is_encrypt === "true",
      is_filepicker: record.is_filepicker === "true",
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (record) => {
    try {
      const row = await form.validateFields();
      // Merge with existing record and call updateData
      const updatedRecord = {
        ...record,
        ...row,
        is_required: row.is_required ? "true" : "false",
        is_encrypt: row.is_encrypt ? "true" : "false",
        is_filepicker: row.is_filepicker ? "true" : "false",
      };
      updateData(updatedRecord);
      setEditingKey("");
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleDelete = async (record) => {
    const res = await showConfirmDlgWidget({
      title: "Delete Confirmation",
      content: `Are you sure you want to delete "${record.name}"?`,
    });
    if (res === false) return;
    deleteData(record.id);
  };

  const uiTypeOptions = Object.entries(UI_TYPE_MAP).map(([value, info]) => ({
    value,
    label: info.label,
  }));

  const columns = [
    {
      title: "#",
      dataIndex: "order",
      width: 50,
      editable: true,
      inputType: "number",
      sorter: { compare: (a, b) => Number(a.order || 0) - Number(b.order || 0), multiple: 2 },
      defaultSortOrder: "ascend",
      render: (order) => (
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          background: "#f0f0f0",
          borderRadius: "50%",
          fontSize: 12,
          fontWeight: 600
        }}>
          {order}
        </span>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 150,
      editable: true,
      inputType: "text",
      sorter: (a, b) => String(a.name || "").localeCompare(String(b.name || "")),
      render: (name, record) => {
        const isRequired = record.is_required === "true";
        const isEncrypt = record.is_encrypt === "true";
        const isFilePicker = record.is_filepicker === "true";
        return (
          <Space size={4}>
            <span style={{ fontWeight: 500 }}>{name}</span>
            {isRequired && (
              <Tooltip title="Required">
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 12 }} />
              </Tooltip>
            )}
            {isEncrypt && (
              <Tooltip title="Encrypted">
                <LockOutlined style={{ color: "#faad14", fontSize: 12 }} />
              </Tooltip>
            )}
            {isFilePicker && (
              <Tooltip title="File Picker">
                <FileOutlined style={{ color: "#1890ff", fontSize: 12 }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Hint",
      dataIndex: "hint_text",
      width: 120,
      editable: true,
      inputType: "text",
      ellipsis: true,
      render: (text) => (
        <span style={{ color: "#666", fontStyle: "italic", fontSize: 12 }}>
          {text || "-"}
        </span>
      ),
    },
    {
      title: "Column",
      dataIndex: "reg_column_name",
      width: 130,
      ellipsis: true,
      render: (col) => (
        <code style={{
          background: "#f5f5f5",
          padding: "2px 6px",
          borderRadius: 3,
          fontSize: 11
        }}>
          {col}
        </code>
      ),
    },
    {
      title: "Group",
      dataIndex: "group_title",
      width: 130,
      sorter: { compare: (a, b) => String(a.group_title || "").localeCompare(String(b.group_title || "")), multiple: 1 },
      defaultSortOrder: "ascend",
      render: (title, record) => (
        <Tooltip title={GROUP_TYPE_MAP[record.group_type] || "Other"}>
          <span style={{ fontSize: 12 }}>{title}</span>
        </Tooltip>
      ),
    },
    {
      title: "Type",
      dataIndex: "ui_type",
      width: 100,
      editable: true,
      inputType: "select",
      options: uiTypeOptions,
      render: (type) => {
        const info = UI_TYPE_MAP[type] || { label: "Unknown", icon: <FormOutlined />, color: "default" };
        return (
          <Tag color={info.color} icon={info.icon} style={{ fontSize: 11 }}>
            {info.label}
          </Tag>
        );
      },
    },
    {
      title: "Dropdown Options",
      dataIndex: "dropdown_list",
      width: 120,
      ellipsis: true,
      render: (list, record) => {
        if (record.ui_type !== "3" || !list) return <span style={{ color: "#999" }}>-</span>;
        const count = list.split(",").length;
        return (
          <Tooltip title={list}>
            <Tag color="purple" style={{ fontSize: 11 }}>{count} options</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Req",
      dataIndex: "is_required",
      width: 50,
      align: "center",
      editable: true,
      inputType: "switch",
      render: (val) => (
        val === "true" ? (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        ) : (
          <span style={{ color: "#d9d9d9" }}>-</span>
        )
      ),
    },
    {
      title: "Enc",
      dataIndex: "is_encrypt",
      width: 50,
      align: "center",
      editable: true,
      inputType: "switch",
      render: (val) => (
        val === "true" ? (
          <LockOutlined style={{ color: "#faad14" }} />
        ) : (
          <span style={{ color: "#d9d9d9" }}>-</span>
        )
      ),
    },
    {
      title: "Action",
      dataIndex: "operator",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space size={8}>
            <Tooltip title="Save">
              <SaveOutlined
                onClick={() => save(record)}
                style={{ color: "#52c41a", cursor: "pointer", fontSize: 16 }}
              />
            </Tooltip>
            <Tooltip title="Cancel">
              <CloseOutlined
                onClick={cancel}
                style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 16 }}
              />
            </Tooltip>
          </Space>
        ) : (
          <Space size={8}>
            <Tooltip title="Edit">
              <EditOutlined
                disabled={editingKey !== ""}
                onClick={() => {
                  if (editingKey !== "") {
                    message.warning("Please save or cancel the current edit first");
                    return;
                  }
                  edit(record);
                }}
                style={{
                  color: editingKey !== "" ? "#d9d9d9" : "#1890ff",
                  cursor: editingKey !== "" ? "not-allowed" : "pointer",
                  fontSize: 16
                }}
              />
            </Tooltip>
            <Tooltip title="Full Edit">
              <FormOutlined
                onClick={() => updateData(record)}
                style={{ color: "#722ed1", cursor: "pointer", fontSize: 16 }}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <DeleteOutlined
                onClick={() => handleDelete(record)}
                style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 16 }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        options: col.options,
      }),
    };
  });

  const dataSource = dataList.map((data, index) => {
    return { ...data, key: data.id || index };
  });

  // Sort data by group_title and then by order for alternating row colors
  const sortedData = [...dataSource].sort((a, b) => {
    const groupCompare = String(a.group_title || "").localeCompare(String(b.group_title || ""));
    if (groupCompare !== 0) return groupCompare;
    return Number(a.order || 0) - Number(b.order || 0);
  });

  // Create group color mapping for alternating backgrounds
  const groupColors = {};
  let colorIndex = 0;
  const colors = ["#ffffff", "#fafafa"];
  sortedData.forEach((item) => {
    if (groupColors[item.group_title] === undefined) {
      groupColors[item.group_title] = colors[colorIndex % 2];
      colorIndex++;
    }
  });

  return (
    <Form form={form} component={false}>
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        columns={mergedColumns}
        dataSource={dataSource}
        rowClassName={(record) => {
          const baseClass = "form-ui-table-row";
          const groupClass = groupColors[record.group_title] === "#fafafa" ? "alternate-row" : "";
          const editingClass = isEditing(record) ? "editing-row" : "";
          return `${baseClass} ${groupClass} ${editingClass}`.trim();
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No form fields configured yet"
            />
          ),
        }}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "15", "25", "50"] }}
        size="small"
      />
    </Form>
  );
}

export default withRouter(FormUiTable);
